// This is the React component for Section 5 ("VISION")
// It features a "cutout" text effect and a 2-part reveal animation.

import React, { useLayoutEffect, useRef } from "react";
import "./Section5.css"; // We will create this file next
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

// Import the paper rip image.
// The main background (0_0.jpg) is handled in CSS.
import paperRipImg from "../../../assets/images/PaperRip2.png";

gsap.registerPlugin(ScrollTrigger);

// --- Component Text ---
const titleText = "VISION";
const paragraphText =
  "OUR PROGRAM IS ROOTED IN FUN, CULTIVATED THROUGH CONNECTION, AND CARRIED BY COMMUNITY";

// --- React Component ---
function Section5() {
  // --- Refs ---
  const sectionRef = useRef(null); // The main section
  const ripContainerRef = useRef(null); // The paper rip element
  const paragraphRef = useRef(null); // The paragraph *inside* the rip

  // --- Animation ---
  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      // Create a timeline for our 2-part sequence
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: sectionRef.current,
          start: "top 60%", // Starts when the section is 75% from the top
          toggleActions: "play none none none",
          once: true,
        },
      });

      // --- Animation 1: The Rip Reveals Downwards ---
      // We animate the 'clip-path' from 100% hidden (bottom) to 0% hidden
      tl.fromTo(
        ripContainerRef.current,
        { clipPath: "inset(0 0 100% 0)" }, // from: hidden
        {
          clipPath: "inset(0 0 0% 0)", // to: fully visible
          duration: 1.2, // A little slower for a nice wipe
          ease: "power3.out",
        }
      );

      // --- Animation 2: The Text Reveals from the Left ---
      // This is the same effect from the Hero section
      tl.fromTo(
        paragraphRef.current,
        { clipPath: "inset(0 100% 0 0)" }, // from: hidden
        {
          clipPath: "inset(0 0% 0 0)", // to: fully visible
          duration: 1,
          ease: "power2.out",
        },
        "-=0.7" // Starts 0.7s *before* the rip animation finishes
      );
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  // --- JSX (HTML) ---
  return (
    // We attach the main 'sectionRef' here
    // The main background (0_0.jpg) is applied by this class
    <section className="section-5" ref={sectionRef}>
      
      {/* This container holds the rip image and the text content.
          We will animate this container's clip-path.
      */}
      <div className="section-5-rip-container" ref={ripContainerRef}>
        
        {/* This is just the paper rip PNG. It sits *behind* the text. */}
        <img
          src={paperRipImg}
          alt="Torn paper background"
          className="section-5-rip-bg"
        />

        {/* This div holds the text and sits *on top* of the rip PNG */}
        <div className="section-5-content">
          
          {/* YOUR CUTOUT EFFECT:
            This title is styled in CSS to be "transparent"
            and show the section's fixed background image *through* it.
          */}
          <h2 className="section-5-title">{titleText}</h2>

          {/* We attach the ref to the paragraph for its animation */}
          <p className="section-5-paragraph" ref={paragraphRef}>
            {paragraphText}
          </p>
        </div>

      </div>
    </section>
  );
}

export default Section5;